/**
 * PresenceManager - Client-side presence tracking for Phase 4 Collaboration
 *
 * Manages page presence via PagePresenceChannel WebSocket connection.
 * Tracks which admins are viewing/editing the page.
 *
 * Key features:
 * - Client-driven heartbeat (no server periodically)
 * - Activity state tracking (viewing, editing, idle)
 * - Idle detection (switch to idle after inactivity)
 * - Avatar updates for real-time presence display
 */

// Constants
const HEARTBEAT_INTERVAL = 10000 // 10s heartbeat
const IDLE_TIMEOUT = 60000 // 60s idle detection

// P1-9 FIX: Debug logging helper - only logs in development or when debug flag is set
const DEBUG = typeof window !== 'undefined' && (window.PAGE_EDITOR_DEBUG || window.location?.hostname === 'localhost')
const debugLog = (...args) => DEBUG && console.log(...args)

/**
 * @typedef {Object} PresenceInfo
 * @property {string} admin_id - Admin UUID
 * @property {string} admin_name - Admin full name
 * @property {string} avatar_url - Gravatar URL
 * @property {string} initials - Fallback initials
 * @property {string} activity_state - viewing | editing | idle
 * @property {string|null} current_block_id - Block being edited
 * @property {boolean} is_self - Whether this is the current user
 */

export class PresenceManager {
  /**
   * @param {Object} options
   * @param {string} options.pageId - Current page UUID
   * @param {string} options.currentAdminId - Current admin UUID
   * @param {Object} options.consumer - ActionCable consumer instance
   */
  constructor(options = {}) {
    this.pageId = options.pageId
    this.currentAdminId = options.currentAdminId
    this.consumer = options.consumer

    // Presence state
    /** @type {Map<string, PresenceInfo>} */
    this.presences = new Map()
    /** @type {string} Current activity state */
    this.currentActivity = 'viewing'
    /** @type {string|null} Current block being edited */
    this.currentBlockId = null

    // Callbacks
    /** @type {Set<Function>} Presence change listeners */
    this.presenceChangeListeners = new Set()

    // Timers
    this.heartbeatTimer = null
    this.idleTimer = null

    // Channel subscription
    this.subscription = null

    debugLog('[PresenceManager] Initialized for page:', this.pageId)
  }

  /**
   * Connect to PagePresenceChannel
   * @returns {Promise<void>}
   */
  connect() {
    return new Promise((resolve, reject) => {
      if (!this.consumer) {
        reject(new Error('ActionCable consumer required'))
        return
      }

      this.subscription = this.consumer.subscriptions.create(
        { channel: 'PagePresenceChannel', page_id: this.pageId },
        {
          connected: () => {
            debugLog('[PresenceManager] Connected to PagePresenceChannel')
            this.setupHeartbeatInterval()
            this.setupIdleDetection()
            resolve()
          },
          disconnected: () => {
            debugLog('[PresenceManager] Disconnected from PagePresenceChannel')
            this.clearTimers()
            this.presences.clear()
            this.notifyPresenceChange()
          },
          rejected: () => {
            console.error('[PresenceManager] PagePresenceChannel subscription rejected')
            reject(new Error('Channel subscription rejected'))
          },
          received: (data) => {
            this.handleMessage(data)
          }
        }
      )
    })
  }

  /**
   * Disconnect from channel
   */
  disconnect() {
    this.clearTimers()

    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }

    this.presences.clear()
    debugLog('[PresenceManager] Disconnected')
  }

  /**
   * Send heartbeat to server
   * Called automatically by timer
   */
  sendHeartbeat() {
    this.subscription?.perform('heartbeat')
  }

  /**
   * Update activity state
   * @param {string} state - Activity state (viewing, editing, idle)
   * @param {string|null} blockId - Block ID if editing
   */
  updateActivity(state, blockId = null) {
    // Validate state
    if (!['viewing', 'editing', 'idle'].includes(state)) {
      console.warn('[PresenceManager] Invalid activity state:', state)
      return
    }

    // Skip if no change
    if (this.currentActivity === state && this.currentBlockId === blockId) {
      return
    }

    this.currentActivity = state
    this.currentBlockId = blockId

    // Send to server
    this.subscription?.perform('update_activity', {
      state,
      block_id: blockId
    })

    // Reset idle timer on activity
    if (state !== 'idle') {
      this.resetIdleTimer()
    }

    debugLog('[PresenceManager] Activity updated:', state, blockId)
  }

  /**
   * Get other presences (excluding self)
   * @returns {PresenceInfo[]}
   */
  getOtherPresences() {
    return Array.from(this.presences.values()).filter((p) => !p.is_self)
  }

  /**
   * Get all presences including self
   * @returns {PresenceInfo[]}
   */
  getAllPresences() {
    return Array.from(this.presences.values())
  }

  /**
   * Check if a block is being edited by another user
   * @param {string} blockId - Block UUID
   * @returns {boolean}
   */
  isBlockEditedByOther(blockId) {
    for (const presence of this.presences.values()) {
      if (!presence.is_self &&
          presence.activity_state === 'editing' &&
          presence.current_block_id === blockId) {
        return true
      }
    }
    return false
  }

  /**
   * Get user editing a specific block
   * @param {string} blockId - Block UUID
   * @returns {PresenceInfo|null}
   */
  getBlockEditor(blockId) {
    for (const presence of this.presences.values()) {
      if (!presence.is_self &&
          presence.activity_state === 'editing' &&
          presence.current_block_id === blockId) {
        return presence
      }
    }
    return null
  }

  /**
   * Subscribe to presence changes
   * @param {Function} callback - (presences: PresenceInfo[]) => void
   * @returns {Function} Unsubscribe function
   */
  onPresenceChange(callback) {
    this.presenceChangeListeners.add(callback)
    return () => this.presenceChangeListeners.delete(callback)
  }

  // Private methods

  /**
   * Handle incoming WebSocket message
   * @private
   */
  handleMessage(data) {
    debugLog('[PresenceManager] Received:', data)

    switch (data.event) {
      case 'presences_state':
        this.handlePresencesState(data.presences)
        break
      case 'joined':
        this.handleJoined(data)
        break
      case 'left':
        this.handleLeft(data)
        break
      case 'activity_updated':
        this.handleActivityUpdated(data)
        break
      default:
        debugLog('[PresenceManager] Unknown event:', data.event)
    }
  }

  /**
   * Handle initial presences state
   * P0-7 FIX: Recompute is_self client-side instead of trusting server value
   * (server computes is_self relative to the broadcasting admin, which is wrong for other receivers)
   * @private
   */
  handlePresencesState(presences) {
    this.presences.clear()

    for (const presence of presences) {
      // P0-7 FIX: Always recompute is_self based on this client's admin ID
      presence.is_self = presence.admin_id === this.currentAdminId
      this.presences.set(presence.admin_id, presence)
    }

    debugLog('[PresenceManager] Presences state:', this.presences.size, 'users')
    this.notifyPresenceChange()
  }

  /**
   * Handle user joined event
   * @private
   */
  handleJoined(data) {
    // Don't add self again if already in map
    if (data.admin_id === this.currentAdminId && this.presences.has(data.admin_id)) {
      return
    }

    const presence = {
      admin_id: data.admin_id,
      admin_name: data.admin_name,
      avatar_url: data.avatar_url,
      initials: data.initials,
      activity_state: 'viewing',
      current_block_id: null,
      is_self: data.admin_id === this.currentAdminId
    }

    this.presences.set(data.admin_id, presence)
    debugLog('[PresenceManager] User joined:', data.admin_name)
    this.notifyPresenceChange()
  }

  /**
   * Handle user left event
   * @private
   */
  handleLeft(data) {
    if (this.presences.has(data.admin_id)) {
      const name = this.presences.get(data.admin_id)?.admin_name
      this.presences.delete(data.admin_id)
      debugLog('[PresenceManager] User left:', name)
      this.notifyPresenceChange()
    }
  }

  /**
   * Handle activity updated event
   * @private
   */
  handleActivityUpdated(data) {
    const presence = this.presences.get(data.admin_id)
    if (presence) {
      presence.activity_state = data.activity_state
      presence.current_block_id = data.current_block_id
      debugLog('[PresenceManager] Activity updated for:', data.admin_name, data.activity_state)
      this.notifyPresenceChange()
    }
  }

  /**
   * Setup heartbeat interval (10s)
   * @private
   */
  setupHeartbeatInterval() {
    this.clearHeartbeatTimer()

    this.heartbeatTimer = setInterval(() => {
      this.sendHeartbeat()
    }, HEARTBEAT_INTERVAL)

    debugLog('[PresenceManager] Heartbeat interval started (10s)')
  }

  /**
   * Setup idle detection
   * @private
   */
  setupIdleDetection() {
    // Listen for user activity
    const resetIdle = () => {
      if (this.currentActivity === 'idle') {
        this.updateActivity('viewing')
      }
      this.resetIdleTimer()
    }

    // Track mouse and keyboard activity
    document.addEventListener('mousemove', resetIdle, { passive: true })
    document.addEventListener('keydown', resetIdle, { passive: true })
    document.addEventListener('click', resetIdle, { passive: true })

    // Store cleanup function
    this._idleCleanup = () => {
      document.removeEventListener('mousemove', resetIdle)
      document.removeEventListener('keydown', resetIdle)
      document.removeEventListener('click', resetIdle)
    }

    this.resetIdleTimer()
    debugLog('[PresenceManager] Idle detection started (60s)')
  }

  /**
   * Reset idle timer
   * @private
   */
  resetIdleTimer() {
    this.clearIdleTimer()

    this.idleTimer = setTimeout(() => {
      if (this.currentActivity !== 'idle') {
        debugLog('[PresenceManager] User went idle')
        this.updateActivity('idle')
      }
    }, IDLE_TIMEOUT)
  }

  /**
   * Clear heartbeat timer
   * @private
   */
  clearHeartbeatTimer() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  /**
   * Clear idle timer
   * @private
   */
  clearIdleTimer() {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer)
      this.idleTimer = null
    }
  }

  /**
   * Clear all timers
   * @private
   */
  clearTimers() {
    this.clearHeartbeatTimer()
    this.clearIdleTimer()

    // Cleanup idle detection listeners
    if (this._idleCleanup) {
      this._idleCleanup()
      this._idleCleanup = null
    }
  }

  /**
   * Notify all presence change listeners
   * @private
   */
  notifyPresenceChange() {
    const presences = this.getAllPresences()
    for (const callback of this.presenceChangeListeners) {
      try {
        callback(presences)
      } catch (error) {
        console.error('[PresenceManager] Listener error:', error)
      }
    }
  }
}
